import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Navbar from '../components/Navbar/Navbar';
import Sidebar, { EXPANDED_WIDTH, COLLAPSED_WIDTH } from '../components/Sidebar/Sidebar';
import { useLocalStorage, useMedia } from 'react-use';
import { checkToken } from '../helpers/checkToken';
import { Menu, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { getQueryParam } from '../helpers/Project-Report/utils/params';
import { getUser } from '../helpers/api/users';


const DashboardLayout = () => {
    const [token] = useLocalStorage('token', '');
    const [user, setUser] = useState({});
    const [isUserLoaded, setIsUserLoaded] = useState(false);

    const isMobile = useMedia('(max-width: 768px)');
    const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
    const [searchParams] = useSearchParams();
    const onIframe = getQueryParam(searchParams, 'onIframe'); //hide jika di akses dari iframe
    
    if (!onIframe) {
        checkToken(token);
    }

    useEffect(() => {
        let active = true;

        if (onIframe || !token) {
            setUser({});
            setIsUserLoaded(true);
            return () => {
                active = false;
            };
        }

        setUser({});
        setIsUserLoaded(false);

        const fetchUser = async () => {
            try {
                const response = await getUser(token);
                const responseBody = await response.json();

                if (active && responseBody.status === true) {
                    setUser(responseBody.data);
                }
            } catch {
                if (active) {
                    setUser({});
                }
            } finally {
                if (active) {
                    setIsUserLoaded(true);
                }
            }
        };

        fetchUser();

        return () => {
            active = false;
        };
    }, [onIframe, token]);

    useEffect(() => {
        setIsSidebarOpen(!isMobile);
    }, [isMobile]);

    useEffect(() => {
        if (sidebarCollapsed) return;
        const timer = setTimeout(() => setSidebarCollapsed(true), 3000);
        return () => clearTimeout(timer);
    }, [sidebarCollapsed]);

    const currentSidebarWidth = sidebarCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

    return (
        <>
            {onIframe ? (
                <main style={{ minHeight: '100vh', background: '#f5f9fc', position: 'relative' }}>
                    <Outlet context={{ user, isUserLoaded }} />
                </main>
            ) : (
                <div
                    style={{
                        display: 'flex',
                        minHeight: '100vh',
                        background: '#f5f9fc',
                        position: 'relative',
                    }}
                >
                    {/* Sidebar Wrapper */}
                    <div
                        onMouseEnter={() => setSidebarCollapsed(false)}
                        onMouseLeave={() => setSidebarCollapsed(true)}
                        onClick={() => { if (sidebarCollapsed) setSidebarCollapsed(false); }}
                        style={{
                            position: isMobile ? 'fixed' : 'absolute',
                            top: 0,
                            left: 0,
                            height: '100vh',
                            zIndex: 100,
                            transform: isSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
                            transition: 'transform 0.3s ease-in-out, width 0.25s ease',
                            width: isMobile ? 'auto' : `${currentSidebarWidth}px`,
                            visibility: !isSidebarOpen && isMobile ? 'hidden' : 'visible',
                        }}
                    >
                        <Sidebar
                            collapsed={sidebarCollapsed}
                            onClose={() => setSidebarCollapsed(true)}
                            user={user}
                        />
                    </div>

                    {/* Mobile Overlay */}
                    {isMobile && isSidebarOpen && (
                        <div 
                            onClick={() => setIsSidebarOpen(false)}
                            style={{
                                position: 'fixed', inset: 0, zIndex: 40,
                                background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
                            }}
                        />
                    )}
                    
                    {/* Main Content Area */}
                    <div
                        style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth: 0,
                            overflow: 'auto',
                            height: '100vh',
                            marginLeft: isMobile ? 0 : `${COLLAPSED_WIDTH}px`,
                            position: 'relative',
                        }}
                    >
                        {/* Desktop Blur Overlay */}
                        {!isMobile && (
                            <div 
                                style={{
                                    position: 'fixed',
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: `${COLLAPSED_WIDTH}px`,
                                    zIndex: 90,
                                    background: 'rgba(245, 249, 252, 0.3)',
                                    backdropFilter: 'blur(4px)',
                                    opacity: sidebarCollapsed ? 0 : 1,
                                    visibility: sidebarCollapsed ? 'hidden' : 'visible',
                                    transition: 'opacity 0.3s ease, visibility 0.3s ease',
                                    pointerEvents: 'none',
                                }}
                            />
                        )}
                        {/* Mobile Header / Toggle */}
                        {isMobile && (
                            <div
                                style={{
                                    padding: '12px 16px', background: '#fff', borderBottom: '1px solid #e2e8f0',
                                    display: 'flex', alignItems: 'center', gap: '12px', position: 'sticky', top: 0, zIndex: 30
                                }}
                            >
                                <button 
                                    onClick={() => setIsSidebarOpen(true)}
                                    style={{
                                        background: 'transparent', border: 'none', cursor: 'pointer',
                                        padding: '4px', display: 'flex', alignItems: 'center', color: '#1e293b'
                                    }}
                                >
                                    <Menu size={24} />
                                </button>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <img 
                                        src="https://portal.geloraaksara.co.id/assets/img/image/LogoG.png" 
                                        alt="Logo" 
                                        style={{
                                            width: '32px', height: '32px', borderRadius: '8px', objectFit: 'contain'
                                        }} 
                                    />
                                    <div>
                                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>GELORA GROUP</div>
                                        <div style={{ fontSize: '9px', color: '#94a3b8', letterSpacing: '0.5px' }}>Dashboard v2.1</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <main style={{ flex: 1, padding: '0px', position: 'relative' }}>
                            <Outlet context={{ user, isUserLoaded }} />
                        </main>
                    </div>
                </div>
            )}
        </>
    );
};

export default DashboardLayout;
